import type {HorizonModule, ModuleAPI, ModuleContext} from './types'
import {registerSlot, unregisterModuleSlots} from '#/modules/slots'

declare global {
  // eslint-disable-next-line no-var
  var __HORIZON_ACTIVE_MODULES__: Set<string> | undefined
}

function getActive(): Set<string> {
  return (
    globalThis.__HORIZON_ACTIVE_MODULES__ ??
    (globalThis.__HORIZON_ACTIVE_MODULES__ = new Set())
  )
}

// 1) List of module loaders (dynamic imports keep private code out of core bundles until needed)
type ModuleLoader = () => Promise<{default: HorizonModule}>

// 2) Central list. Start with empty or one “demo” module.
const loaders: ModuleLoader[] = [
  () => import('./builtins/demoModule'),
  () => import('./builtins/themesModule'),

  // Example placeholder:
  // () => import('./builtins/demoModule'),
  // Later:
  // () => import('./horizon-private/index'),
]


export async function initModules(ctx: ModuleContext) {
  const baseApi: Pick<ModuleAPI, 'log'> = {
    log: (...args) => console.log('[modules]', ...args),
  }
  const active = getActive()

  // Load modules sequentially for predictable behavior
  for (const load of loaders) {
    let mod: HorizonModule | null = null
    try {
      const imported = await load()
      mod = imported.default
    } catch (e) {
      baseApi.log('failed to import module:', e)
      continue
    }

    try {
      const ok = await mod.canActivate(ctx)

      // 🔥 NEW: If it was active but no longer allowed, unload it.
      if (!ok) {
        if (active.has(mod.id)) {
          baseApi.log('deactivating', mod.id)
          unregisterModuleSlots(mod.id)
          active.delete(mod.id)
        }
        continue
      }

      if (active.has(mod.id)) {
        baseApi.log('already active', mod.id)
        continue
      }

      const api: ModuleAPI = {
        ...baseApi,
        slots: {
          register: (slotId, renderer) => registerSlot(mod!.id, slotId, renderer),
        },
      }

      baseApi.log('activating', mod.id)
      await mod.register(api, ctx)
      active.add(mod.id)
    } catch (e) {
      baseApi.log('module activation failed:', mod?.id, e)
    }
  }
}
